// Network Plus pre-site surveys. Network Plus sends a sheet per area (e.g.
// "Nano LE (Leicester).xlsx") listing jobs to survey before they are booked.
// We survey each one on site, fill in columns 7-14 of their sheet and send it
// back. Jobs are known by Network Plus's Job Number (e.g. 000041966709-0010).
//
// Job Status (column 6) moves forward only:
//   To Presite -> Presited (surveyed) -> Planned (the customer gave a date)
//   -> Completed (installed; set later, from Network Plus's confirmed jobs).

export const SURVEY_STATUSES = Object.freeze(['To Presite', 'Presited', 'Planned', 'Completed']);
const RANK = Object.fromEntries(SURVEY_STATUSES.map((s, i) => [s, i]));
export const surveyStatusRank = (s) => RANK[s] ?? -1;

// The further on of two statuses (an import never moves a job back).
export const laterStatus = (a, b) => (surveyStatusRank(b) > surveyStatusRank(a) ? b : a);

// After a survey: Planned if the customer gave a planned date, else Presited.
// A Completed job stays Completed.
export function statusAfterSurvey(current, plannedDate) {
  if (current === 'Completed') return current;
  return plannedDate ? 'Planned' : 'Presited';
}

// What Network Plus see as a job's gang (their page, and every Excel/PDF
// export): only once the job has been surveyed. Before that the gang is our
// own match by postcode, for planning, not something we've told them.
export const gangForNetworkPlus = (job) => (job?.surveyedAt || job?.surveyedOn ? job.gangName || null : null);

// A gang number is Network Plus's: five digits, e.g. 18648.
export const isGangNumber = (v) => /^\d{5}$/.test(String(v ?? '').trim());

// Column 7, exactly as Network Plus spells them (the sheet goes back to them).
export const PRESITE_STATUSES = Object.freeze([
  'Standard Dig - To Plan',
  'Private',
  'C/W - No TM',
  'C/W & TM',
  'TM ( 2Way-4Way)',
  'Rd Closure',
  'Lamppost Support',
  'Joint Supply',
  "Can't Locate",
  'MST Over 1.2',
]);

// Column 9's choices. "Other" asks the surveyor to say what it is, and what
// they type is what goes in the sheet.
export const SURFACE_TYPES = Object.freeze(['Grass', 'Softsoil', 'Footway', 'Block Paving', 'Flags', 'Concrete', 'Carriage Way']);
export const SURFACE_OTHER = 'Other (Specify)';

// Suggestions for column 11 (from the lists on their Sheet2); any text is fine.
export const PERMIT_CONDITION_SUGGESTIONS = Object.freeze(['Parked Cars', 'Precone', 'Road Closure']);

// The sheet's columns we fill in, in order (7-14), with their headings.
export const SURVEY_FIELDS = Object.freeze([
  { key: 'presiteStatus', col: 7, label: 'Presite Status' },
  { key: 'presiteNotes', col: 8, label: 'Presite Notes' },
  { key: 'surfaceType', col: 9, label: 'Surface Type' },
  { key: 'mstLocation', col: 10, label: 'MST Location' },
  { key: 'permitConditions', col: 11, label: 'Permit conditions required' },
  { key: 'permitLength', col: 12, label: 'Permit length required' },
  { key: 'gangName', col: 13, label: 'Gang Name' },
  { key: 'plannedDate', col: 14, label: 'Planned Date' },
]);

// "Nano LE (Leicester).xlsx" -> { code: 'LE', name: 'Leicester' }.
export function areaFromFileName(fileName) {
  const base = String(fileName || '').replace(/\.[a-z0-9]+$/i, '');
  const m = /\b([A-Z]{1,2})\s*\(([^)]+)\)/.exec(base);
  if (m) return { code: m[1], name: m[2].trim() };
  return { code: '', name: '' };
}

// A postcode area for matching: upper case, single space, e.g. "LE13 1LJ".
export function tidyPostcode(pc) {
  const s = String(pc || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length > 3 ? `${s.slice(0, -3)} ${s.slice(-3)}` : s;
}

// A gang covers postcode prefixes: an area ("LE"), a district ("LE13") or a
// sector ("LE13 1"). A prefix of letters only matches the whole letter part
// of the postcode, so "L" (Liverpool) never matches "LE13 1LJ".
const tidyPrefix = (p) => String(p || '').toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

export function prefixMatches(prefix, postcode) {
  const p = tidyPrefix(prefix);
  const pc = tidyPostcode(postcode);
  if (!p || !pc) return false;
  if (/^[A-Z]+$/.test(p)) return /^[A-Z]+/.exec(pc)?.[0] === p;
  const [outP, inP = ''] = p.split(' ');
  const [outPc, inPc = ''] = pc.split(' ');
  return outP === outPc && inPc.startsWith(inP);
}

// Clean a list of prefixes typed by the office ("LE13, le14 ; CH1").
export function parsePrefixes(text) {
  const parts = String(text || '').split(/[,;\n]+/).map(tidyPrefix).filter(Boolean);
  return [...new Set(parts)].filter((p) => /^[A-Z]{1,2}(\d[A-Z\d]?( \d)?)?$/.test(p));
}

// The gang whose prefix fits the postcode best (longest prefix wins), or null.
// gangs: [{ teamId, prefixes: ['LE13', 'LE14 2'] }]
export function gangForPostcode(gangs, postcode) {
  let best = null;
  let bestLen = -1;
  for (const g of gangs || []) {
    for (const p of g.prefixes || []) {
      if (prefixMatches(p, postcode) && p.length > bestLen) {
        best = g;
        bestLen = p.length;
      }
    }
  }
  return best;
}

// Straight-line distance in km (for "nearest first" on the phone).
export function distanceKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad((b.lon ?? b.lng) - (a.lon ?? a.lng));
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

// --- The survey on site (after Modular Plus's "600 - Off Site - FROPT") ---
//
// Asked in the app section by section, as tap buttons. A question with
// `showIf` is only asked (and only needed) when that's true of the answers so
// far. 'yesno' answers are 'Yes' / 'No'; a choice list with 'Other' asks the
// surveyor to say what (kept as <key>Other). Photos go in named slots:
// 'required' needs `min` photos, 'or_reason' needs a photo or a reason it
// couldn't be taken (kept in answers.photoSkip[slot]), 'optional' needs none.
// The answers are kept with the job (survey_jobs.details), apart from
// columns 7-14 of Network Plus's sheet, which are still filled in as before.
const yes = (k) => (d) => d[k] === 'Yes';
export const SURVEY_SECTIONS = Object.freeze([
  {
    id: 'visit', title: 'Visit',
    questions: [
      { key: 'visitType', label: 'Visit type', type: 'choice', options: ['Appointment', 'No appointment'], required: true },
      { key: 'inApptTime', label: 'Attended within the appointment time?', type: 'yesno', required: true, showIf: (d) => d.visitType === 'Appointment' },
      { key: 'propertyType', label: 'Property type', type: 'choice', options: ['Detached', 'Semi-detached', 'Terraced', 'Flat', 'Bungalow', 'Commercial'], required: true },
      { key: 'onSiteAction', label: 'On site action', type: 'choice', options: ['Survey only', 'Abort', 'No access'], required: true },
    ],
  },
  {
    id: 'supply', title: 'Supply',
    questions: [
      { key: 'whyNot', label: 'Why can the job not be completed today?', type: 'choice', options: ['Survey for digs', 'No access to the internal stop tap', 'Customer not home', 'Customer refused', 'Can’t locate the supply', 'Other'], required: true },
      { key: 'supplyType', label: 'Type of supply', type: 'choice', options: ['Single supply', 'Joint supply', 'Shared supply'], required: true },
      { key: 'bbNeeded', label: 'Boundary box install or replacement needed?', type: 'yesno', required: true },
    ],
    photos: [{ slot: 'entry', label: 'Point of entry (for a new boundary box and external meter)', need: 'or_reason', min: 1 }],
  },
  {
    id: 'dig', title: 'Boundary box and dig site', showIf: yes('bbNeeded'),
    questions: [
      { key: 'excavationPosition', label: 'Excavation position', type: 'choice', options: ['Footway', 'Verge', 'Carriageway', 'Private land'], required: true },
      { key: 'bbSameStreet', label: 'Boundary box on the same street as the customer’s address?', type: 'yesno', required: true },
      { key: 'bbDepth', label: 'Boundary box depth (indicative)', type: 'choice', options: ['Under 0.75m', '0.75m to 1.2m', 'Over 1.2m', 'Unknown'], required: true },
      { key: 'tmRequired', label: 'Traffic management required', type: 'choice', options: ['None', 'Some C/W incursion', 'Full C/W', '2-way lights', '4-way lights', 'Road closure'], required: true },
      { key: 'roadType', label: 'Road type', type: 'choice', options: ['Two-way traffic', 'One-way', 'Single track', 'Private road'], required: true },
      { key: 'roadSpeed', label: 'Road speed', type: 'choice', options: ['20mph', '30mph', '40mph', '50mph or more'], required: true },
      { key: 'roadWidth', label: 'Road width', type: 'choice', options: ['Under 3.5m', '3.5 to 5.5m', 'Over 5.5m'], required: true },
      { key: 'footwayWidth', label: 'Footway width (install side)', type: 'choice', options: ['No footway', 'Under 1m', '1m to 2.5m', 'Over 2.5m'], required: true },
      { key: 'oppositeFootway', label: 'Footway on the opposite side?', type: 'yesno', required: true },
      { key: 'junction50', label: 'Junction within 50m?', type: 'yesno', required: true },
      { key: 'school250', label: 'School within 250m?', type: 'yesno', required: true },
      { key: 'lights50', label: 'Traffic lights within 50m?', type: 'yesno', required: true },
      { key: 'busStop50', label: 'Bus stop within 50m?', type: 'yesno', required: true },
      { key: 'pole05', label: 'Lamp post or telegraph pole within 0.5m?', type: 'yesno', required: true },
      { key: 'scarring1m', label: 'Other utility scarring or surface boxes within 1m of the dig?', type: 'yesno', required: true },
      { key: 'parking', label: 'Parking concerns or restrictions for the dig?', type: 'yesno', required: true },
      { key: 'excavationSurface', label: 'Excavation surface', type: 'choice', options: ['Unmade', 'Grass', 'Softsoil', 'Footway', 'Block Paving', 'Flags', 'Concrete', 'Carriage Way', 'Other'], required: true },
      { key: 'digLocation', label: 'Location of the dig', hint: 'e.g. 1.5m to the right of the driveway and 0.5m from the carriageway', type: 'text', required: true },
      { key: 'suppliesInDig', label: 'Number of supplies in the dig area', type: 'number', min: 1, max: 20, required: true },
    ],
    photos: [
      { slot: 'dig', label: 'The proposed dig area', need: 'required', min: 1 },
      { slot: 'street', label: 'Up and down the street from the dig point', need: 'required', min: 2 },
      { slot: 'pipework', label: 'Pipework exposed before installation', need: 'or_reason', min: 1 },
      { slot: 'stoptap', label: 'Internal stop tap', need: 'or_reason', min: 1 },
      { slot: 'drawn', label: 'Drawn survey (your sketch)', need: 'or_reason', min: 1 },
    ],
  },
  {
    id: 'close', title: 'Close off',
    questions: [
      { key: 'proForma', label: 'Pro-forma required?', type: 'yesno', required: true },
      { key: 'justification', label: 'Justification for the on site action', hint: 'Clear comments are needed for every outcome', type: 'text', required: true },
      { key: 'supplyBackOn', label: 'Supply turned back on?', type: 'choice', options: ['Yes', 'No', 'Wasn’t turned off'], required: true },
    ],
    photos: [{ slot: 'extra', label: 'Any other photos', need: 'optional', min: 0 }],
  },
  {
    id: 'customer', title: 'Customer',
    questions: [
      { key: 'preCalled', label: 'Did you pre-call the customer?', type: 'yesno', required: true },
      { key: 'feedback', label: 'How were feedback and next steps given to the customer?', type: 'choice', options: ['Informed on site', 'Calling card', 'Phone call', 'Not informed'], required: true },
      { key: 'cardLeft', label: 'Have you left a calling card?', type: 'yesno', required: true },
      { key: 'satisfied', label: 'Was the customer satisfied?', type: 'choice', options: ['Yes', 'No', 'Not asked'], required: true },
      { key: 'postWorks', label: 'Is the customer available or willing to do a post works survey?', type: 'yesno', required: true },
    ],
    photos: [{ slot: 'card', label: 'The calling card', need: 'or_reason', min: 1, showIf: yes('cardLeft') }],
  },
]);

export const SURVEY_QUESTIONS = Object.freeze(SURVEY_SECTIONS.flatMap((s) => (s.questions || []).map((q) => ({ ...q, section: s.id }))));
export const SURVEY_PHOTO_SLOTS = Object.freeze(SURVEY_SECTIONS.flatMap((s) => (s.photos || []).map((p) => ({ ...p, section: s.id }))));
const SLOT_KEYS = new Set(SURVEY_PHOTO_SLOTS.map((p) => p.slot));
export const isSurveyPhotoSlot = (s) => SLOT_KEYS.has(s);

// Whether a question / photo slot is asked, given the answers so far.
export function surveyAsked(item, d = {}) {
  const s = SURVEY_SECTIONS.find((x) => x.id === item.section);
  return (!s?.showIf || s.showIf(d)) && (!item.showIf || item.showIf(d));
}

// What's still needed before the survey can be sent: the labels of
// unanswered questions and of photo slots short of photos (or, for
// 'or_reason', of a reason). photoCounts: { slot: n }.
export function surveyMissing(d = {}, photoCounts = {}) {
  const out = [];
  for (const q of SURVEY_QUESTIONS) {
    if (!q.required || !surveyAsked(q, d)) continue;
    const v = d[q.key];
    if (v == null || String(v).trim() === '') out.push(q.label);
    else if (q.type === 'choice' && v === 'Other' && !String(d[`${q.key}Other`] || '').trim()) out.push(`${q.label}: say what`);
  }
  for (const p of SURVEY_PHOTO_SLOTS) {
    if (p.need === 'optional' || !surveyAsked(p, d)) continue;
    const n = photoCounts[p.slot] || 0;
    if (n >= p.min) continue;
    if (p.need === 'or_reason' && n === 0 && String(d.photoSkip?.[p.slot] || '').trim()) continue;
    out.push(p.min > 1 ? `Photos: ${p.label} (${p.min})` : `Photo: ${p.label}`);
  }
  return out;
}

// The answers as sent, tidied: only known questions, in range and from their
// lists; answers to questions no longer asked are dropped.
export function cleanSurveyDetails(raw) {
  const d = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  for (const q of SURVEY_QUESTIONS) {
    const v = d[q.key];
    if (v == null || v === '') continue;
    if (q.type === 'number') {
      const n = Math.round(Number(v));
      if (Number.isFinite(n) && n >= (q.min ?? 0) && n <= (q.max ?? 1e6)) out[q.key] = n;
    } else if (q.type === 'yesno') {
      if (v === 'Yes' || v === 'No') out[q.key] = v;
    } else if (q.type === 'choice') {
      if (q.options.includes(v)) {
        out[q.key] = v;
        if (v === 'Other') out[`${q.key}Other`] = String(d[`${q.key}Other`] || '').trim().slice(0, 200);
      }
    } else {
      out[q.key] = String(v).trim().slice(0, 2000);
    }
  }
  for (const q of SURVEY_QUESTIONS) {
    if (q.key in out && !surveyAsked(q, out)) {
      delete out[q.key];
      delete out[`${q.key}Other`];
    }
  }
  const skip = {};
  for (const p of SURVEY_PHOTO_SLOTS) {
    const r = String(d.photoSkip?.[p.slot] || '').trim().slice(0, 300);
    if (r && p.need === 'or_reason' && surveyAsked(p, out)) skip[p.slot] = r;
  }
  if (Object.keys(skip).length) out.photoSkip = skip;
  return out;
}

// An answer for showing: 'Other' with what they said.
export function surveyAnswer(q, d = {}) {
  const v = d?.[q.key];
  if (v == null || v === '') return '';
  return v === 'Other' && d[`${q.key}Other`] ? `Other: ${d[`${q.key}Other`]}` : String(v);
}
